import { renderCellInline } from "@/components/reader/cm/inline-render";
import { parseTable, type TableAlign } from "@/components/reader/cm/live-preview";

/** ONE editable-table implementation, mounted by both markdown modes.
 *
 *  It is plain DOM rather than a React component because Live mode needs it
 *  inside a CodeMirror block widget, which owns its own element. Preview mounts
 *  the same function through a thin wrapper, so a table behaves and looks
 *  identical whichever mode you are in — which is the whole point: a table is
 *  built visually, so it should be editable visually, in both places.
 *
 *  Nothing here has to resemble the markdown it compiles to. The cells are just
 *  boxes; the source is regenerated from them on commit. */

/** One cell's text, made safe to live between pipes: a literal `|` is escaped,
 *  and newlines collapse to spaces (a GFM row is one line, and a pasted
 *  paragraph would otherwise tear the table apart). */
export function encodeCell(text: string): string {
  return text.replace(/\r?\n+/g, " ").replace(/\|/g, "\\|").trim();
}

/** Rebuild a whole table's markdown from its current cell texts, padded so the
 *  source stays a readable grid for anyone who opens Source mode. */
export function tableToMarkdown(rows: string[][], align: TableAlign[]): string {
  const cols = Math.max(1, ...rows.map((r) => r.length));
  const cells = rows.map((r) => Array.from({ length: cols }, (_, c) => encodeCell(r[c] ?? "")));
  const width = Array.from({ length: cols }, (_, c) =>
    Math.max(3, ...cells.map((r) => (r[c] ?? "").length)),
  );
  const delim = Array.from({ length: cols }, (_, c) => {
    const a = align[c];
    const w = width[c] ?? 3;
    const left = a === "left" || a === "center";
    const right = a === "right" || a === "center";
    const dashes = "-".repeat(Math.max(1, w - (left ? 1 : 0) - (right ? 1 : 0)));
    return ` ${left ? ":" : ""}${dashes}${right ? ":" : ""} `;
  });
  const line = (r: string[]) => `|${r.map((t, c) => ` ${t.padEnd(width[c] ?? 3)} `).join("|")}|`;
  return [line(cells[0] ?? []), `|${delim.join("|")}|`, ...cells.slice(1).map(line)].join("\n");
}

export interface EditableTableHandle {
  /** The element to put in the document. */
  dom: HTMLTableElement;
  /** Re-fill the cells from new source, unless the user is typing in one. */
  sync: (source: string) => void;
}

/** Build an editable grid for `source`, calling `onCommit` with regenerated
 *  markdown whenever the user finishes an edit. Returns null when the source is
 *  not a table this can render. */
export function createEditableTable(
  source: string,
  onCommit: (nextMarkdown: string) => void,
): EditableTableHandle | null {
  const model = parseTable(source);
  if (!model) return null;

  const table = document.createElement("table");
  table.className = "gm-editable-table";
  // The widget root is NOT editable; only the cells are. Inside CodeMirror the
  // surrounding surface is itself contenteditable, so without this the browser
  // would treat the whole table as editor text and let the caret wander into
  // the structure.
  table.contentEditable = "false";

  /** A cell's SOURCE text. While a cell is focused it holds its raw markdown
   *  (so you edit `**bold**`, not the word "bold"); the rest of the time it
   *  shows compiled markup and the source lives on `data-src`. */
  const cellSource = (el: HTMLElement) =>
    (el === document.activeElement ? el.textContent : el.dataset.src) ?? "";

  const read = () =>
    [...table.querySelectorAll("tr")].map((tr) =>
      [...tr.querySelectorAll<HTMLElement>("th,td")].map(cellSource),
    );

  /** Show the cell's markup compiled — bold as bold, an image as an image.
   *  Without this a table was the one place in the note where markdown stayed
   *  as punctuation, which is exactly backwards for the mode meant to be the
   *  most visual. */
  const showRendered = (el: HTMLElement) => {
    el.textContent = "";
    renderCellInline(el.dataset.src ?? "", el);
  };

  let last = source;
  const commit = () => {
    const next = tableToMarkdown(read(), model.align);
    if (next === last) return;
    last = next;
    onCommit(next);
  };

  const cell = (tag: "th" | "td", text: string, align: TableAlign) => {
    const el = document.createElement(tag);
    el.dataset.src = text;
    el.contentEditable = "true";
    el.tabIndex = 0;
    if (align) el.style.textAlign = align;
    showRendered(el);
    // Focus swaps the cell to its raw markdown so it can be edited as text;
    // blur commits and swaps it back to the compiled view. Same rule the rest
    // of the surface follows — markup is revealed where the caret is.
    el.addEventListener("focus", () => {
      el.textContent = el.dataset.src ?? "";
    });
    el.addEventListener("blur", () => {
      el.dataset.src = el.textContent ?? "";
      commit();
      showRendered(el);
    });
    return el;
  };

  const head = table.appendChild(document.createElement("thead")).insertRow();
  model.head.forEach((text, c) => head.appendChild(cell("th", text, model.align[c] ?? null)));
  const body = table.appendChild(document.createElement("tbody"));
  for (const row of model.body) {
    const tr = body.insertRow();
    model.head.forEach((_, c) => tr.appendChild(cell("td", row[c] ?? "", model.align[c] ?? null)));
  }

  // Keep the grid's own input away from whatever hosts it. In Live mode the
  // host is CodeMirror, whose keymap listens on the surrounding contenteditable
  // — so an unstopped Ctrl+A inside a cell ran the EDITOR's select-all and the
  // next keystroke replaced the entire note. Stopping propagation leaves the
  // browser's native contenteditable behaviour, which is correctly scoped to
  // the single cell.
  for (const type of ["keydown", "keypress", "keyup", "beforeinput", "input", "paste", "cut"]) {
    table.addEventListener(type, (event) => event.stopPropagation());
  }

  table.addEventListener("keydown", (event) => {
    const active = document.activeElement as HTMLElement | null;
    if (!active || !table.contains(active)) return;
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a") {
      // Select-all has to mean "this cell". The cells are `contenteditable`
      // inside a host that is ALSO `contenteditable` (CodeMirror's surface), and
      // the browser's native select-all resolves to the OUTERMOST editing host —
      // so Ctrl+A in a cell selected the entire note and the next keystroke
      // replaced it. This is the browser's default action, not a keymap, so it
      // has to be prevented and re-implemented rather than merely intercepted.
      event.preventDefault();
      const range = document.createRange();
      range.selectNodeContents(active);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      active.blur();
      return;
    }
    if (event.key === "Enter") {
      // A cell is one line — Enter commits rather than inserting a break.
      event.preventDefault();
      active.blur();
      return;
    }
    if (event.key !== "Tab") return;
    const cells = [...table.querySelectorAll<HTMLElement>("th,td")];
    const i = cells.indexOf(active);
    if (i < 0) return;
    const next = event.shiftKey ? i - 1 : i + 1;
    if (next < 0) return;
    event.preventDefault();
    if (next >= cells.length) {
      // Tab past the last cell grows the table, the way a table behaves in a
      // word processor. Commit through the same path so both modes agree.
      const rows = read();
      rows.push(Array.from({ length: model.head.length }, () => ""));
      const nextMd = tableToMarkdown(rows, model.align);
      last = nextMd;
      onCommit(nextMd);
      return;
    }
    cells[next]?.focus();
  });

  const sync = (next: string) => {
    last = next;
    // Never overwrite the cell someone is typing in.
    if (table.contains(document.activeElement)) return;
    const m = parseTable(next);
    if (!m) return;
    const rows = [m.head, ...m.body];
    table.querySelectorAll("tr").forEach((tr, r) => {
      tr.querySelectorAll<HTMLElement>("th,td").forEach((el, c) => {
        const want = rows[r]?.[c] ?? "";
        if (el.dataset.src === want) return;
        el.dataset.src = want;
        showRendered(el);
      });
    });
  };

  return { dom: table, sync };
}
